import useAppContext from "@hooks/useAppContext";
import useSlider from "@libs/slider/hooks/useSlider";
import SectionWrapper from "@modules/homePage/SectionWrapper";
import dynamic from "next/dynamic";
import { useMemo } from "react";
import TopSectionGrid, { ICardBuilder } from "./TopSectionGrid";

const SliderCarousel = dynamic(
  async () => await import("@libs/slider/SliderCarousel"),
  { ssr: false }
);

interface ILandingSection {
  title: string;
  body: string;
  link?: string;
  items: JSX.Element[];
  isTopSection?: boolean;
  horizontalScroll?: boolean;
  gridItemsPerRow?: number;
  gridItemsLimit?: number;
  itemsDesktopSlider?: number;
  hasWhiteDots?: boolean;
  hasHalfIndentation?: boolean;
  topSectionTotal?: number;
  cardBuilder: ICardBuilder;
}

const LandingSection = ({
  title,
  body,
  link,
  items,
  isTopSection,
  horizontalScroll,
  gridItemsPerRow,
  gridItemsLimit,
  itemsDesktopSlider,
  hasWhiteDots,
  hasHalfIndentation,
  topSectionTotal,
  cardBuilder,
}: ILandingSection): JSX.Element => {
  const { isMobile } = useAppContext();

  const sliderConfig = useMemo(() => {
    return !hasWhiteDots
      ? {
          desktop: {
            slidesToShow: itemsDesktopSlider,
          },
          mobile: {
            class: "mobile-carousel",
            variableWidth: true,
          },
        }
      : {
          desktop: {
            class: "white-dots-carousel",
            slidesToShow: itemsDesktopSlider,
          },
          mobile: {
            class: "white-dots-carousel",
            arrows: false,
            dots: true,
            variableWidth: true,
          },
        };
  }, [hasWhiteDots]);

  const { sliderSettings } = useSlider(sliderConfig);

  return items?.length ? (
    <div className={`${isTopSection ? "order-1" : "order-2"}`}>
      <SectionWrapper
        key={`${title}_section`}
        title={title}
        body={body}
        link={link}
        hasDots={!isMobile}
        hasHalfIndentation={hasHalfIndentation}
      >
        <>
          {isTopSection ? (
            <TopSectionGrid
              key={`${title}_grid`}
              items={items}
              gridItemsPerRow={gridItemsPerRow}
              gridItemsLimit={gridItemsLimit}
              total={topSectionTotal ?? 0}
              cardBuilder={cardBuilder}
            />
          ) : horizontalScroll ? (
            items.length && (
              <>
                <SliderCarousel
                  key={`${title}_slider`}
                  components={items}
                  settings={sliderSettings}
                />
              </>
            )
          ) : (
            items
          )}
        </>
      </SectionWrapper>
    </div>
  ) : (
    <></>
  );
};

export default LandingSection;
